import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, useParams } from "react-router-dom";
  import { Typography, Box } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import clsx from "clsx";
import Navbar from "components/Navbar";
import DarkLayout from "components/Shared/DarkLayout";
import {
  getAllPermissions,
  getPermissionById,
  selectPermission,
  selectPermissions,
  selectPermissionsStatus,
} from "redux/slices/permissionsSlice";
import { selectUser } from "redux/slices/userSlice";
import { PATHS } from "util/appConstants";
import { getPermissionTitle } from "util/helpers";
import CheckIcon from "@material-ui/icons/Check";
import XIcon from "@material-ui/icons/Clear";

const useStyles = makeStyles({
  _heading: {
    color: "#F5F5F5",
    font: "normal normal normal 28px/40px Questrial",
  },
  _edit: {
    color: "#6F9CEB",
    width: "22px",
    height: "22px",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      transform: "scale(1.3)",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _editbox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  _infoheading: {
    color: "#F5F5F5",
    font: "normal normal medium 22px/32px Roboto",
    marginTop: "44px",
  },
  _head: {
    color: "#F5F5F5",
    opacity: "0.6",
    font: "normal normal normal 12px/24px Roboto",
    marginRight: "32px",
  },
  _basicdetailhead: {
    display: "flex",
    marginTop: "24px",
    flexWrap: "wrap",
    gap: "32px",
  },
  _basicdetail: {
    display: "flex",
  },
  _detail: {
    color: "#F5F5F5",
    marginRight: "32px",
    font: "normal normal normal 18px/24px Roboto",
  },
  _name: {
    letterSpacing: "0px",
  },
  _width11: {},
  _margintop80: {
    marginTop: "40px",
  },
  _adressbox: {
    marginTop: "24px",
  },
  _buttonbox: {
    display: "flex",
    margin: "0 16px",
  },
  _edittext: {
    width: "24px",
    height: "16px",
    color: "#6F9CEB",
    font: "normal normal normal 14px / 20px Roboto",
    padding: "4px 8px",
    display: "none",
    position: "absolute",
    marginLeft: "20px",
    transition: "all 0.3s ease-in-out",
  },
  ruleBadge: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: "#2A2A2A",
    color: "#FFFFFF",
    padding: "4px 12px",
    borderRadius: "16px",
    marginRight: "8px",
    marginBottom: "8px",
    fontSize: "14px",
    border: "1px solid #444",
  },
});

const parseJson = (val) => {
  if (!val) return null;
  if (typeof val === "object") return val;
  try {
    return JSON.parse(val);
  } catch (e) {
    return null;
  }
};

const PermissionDetail = () => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();

  const user = useSelector(selectUser);
  const loading = useSelector(selectPermissionsStatus);
  const permission = useSelector(selectPermission);
  const allPermissions = useSelector(selectPermissions);

  const canEditPermissions =
    user?.isSuperAdmin ||
    Boolean(user?.admin && import.meta.env.VITE_PERMISSION_EDITABLE_BY_ADMIN === "true");

  useEffect(() => {
    dispatch(getAllPermissions());
  }, [dispatch]);

  useEffect(() => {
    if (id && !loading) {
      dispatch(getPermissionById(id));
    }
  }, [dispatch, id]);

  const editHandler = () => {
    history.push(PATHS.admin.permissionEdit?.replace(":id", id));
  };

  const parsedTitle = parseJson(permission?.title);
  const parsedDescription = parseJson(permission?.description);
  const parsedRules = parseJson(permission?.rules);

  const dependsOn = Array.isArray(parsedRules?.depends_on) ? parsedRules.depends_on : [];
  const operator = parsedRules?.operator || "AND";
  const exclusiveWith = Array.isArray(parsedRules?.exclusive_with) ? parsedRules.exclusive_with : [];

  const getPermissionLabelByName = (name) => {
    const found = allPermissions.find(
      (p) => p.name === name && p.module?.toLowerCase() === permission?.module?.toLowerCase()
    );
    return getPermissionTitle(found || { name, module: permission?.module }, i18n);
  };

  return (
    <>
      <Navbar />
      <DarkLayout doublebar loading={loading || !permission}>
        <div className={classes._editbox}>
          <Typography className={classes._heading} variant="h4">
            {getPermissionTitle(permission, i18n)}
          </Typography>
          {canEditPermissions && (
            <Box className={classes._buttonbox}>
              <EditIcon onClick={editHandler} className={classes._edit} />
              <Typography component="span" className={clsx(classes._edittext, "edittag")}>
                {t("Edit")}
              </Typography>
            </Box>
          )}
        </div>

        <Typography className={classes._infoheading} variant="h5">
          {t("Basic Data")}
        </Typography>
        <div className={classes._basicdetailhead}>
          <div>
            <Typography className={clsx(classes._head, classes._width11)} variant="h6">
              {t("Id")}
            </Typography>
            <Typography className={clsx(classes._detail, classes._width11)} variant="h6">
              {permission?.id}
            </Typography>
          </div>
          <div>
            <Typography className={clsx(classes._head, classes._name)} variant="h6">
              {t("System Name")}
            </Typography>
            <Typography className={clsx(classes._name, classes._detail)} variant="h6">
              {permission?.name}
            </Typography>
          </div>
          <div>
            <Typography className={clsx(classes._head, classes._name)} variant="h6">
              {t("Module")}
            </Typography>
            <Typography className={clsx(classes._name, classes._detail)} variant="h6">
              {t(permission?.module || "")}
            </Typography>
          </div>
          <div>
            <Typography className={clsx(classes._head, classes._name)} variant="h6">
              {t("Default Value")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {permission?.default ? <CheckIcon /> : <XIcon />}
            </Typography>
          </div>
          <div>
            <Typography className={clsx(classes._head, classes._name)} variant="h6">
              {t("Sort Order")}
            </Typography>
            <Typography className={clsx(classes._detail, classes._width11)} variant="h6">
              {permission?.sort_order ?? 0}
            </Typography>
          </div>
        </div>

        <Typography className={clsx(classes._infoheading, classes._margintop80)} variant="h5">
          {t("Title, Description & Sorting")}
        </Typography>
        <div className={classes._basicdetailhead}>
          <div>
            <Typography className={clsx(classes._head, classes._name)} variant="h6">
              {t("Title (EN)")}
            </Typography>
            <Typography className={clsx(classes._name, classes._detail)} variant="h6">
              {parsedTitle?.en || "-"}
            </Typography>
          </div>
          <div>
            <Typography className={clsx(classes._head, classes._name)} variant="h6">
              {t("Title (DE)")}
            </Typography>
            <Typography className={clsx(classes._name, classes._detail)} variant="h6">
              {parsedTitle?.de || "-"}
            </Typography>
          </div>
        </div>
        <div className={classes._basicdetailhead}>
          <div>
            <Typography className={clsx(classes._head, classes._name)} variant="h6">
              {t("Description (EN)")}
            </Typography>
            <Typography className={clsx(classes._name, classes._detail)} variant="h6">
              {parsedDescription?.en || "-"}
            </Typography>
          </div>
          <div>
            <Typography className={clsx(classes._head, classes._name)} variant="h6">
              {t("Description (DE)")}
            </Typography>
            <Typography className={clsx(classes._name, classes._detail)} variant="h6">
              {parsedDescription?.de || "-"}
            </Typography>
          </div>
        </div>

        <Typography className={clsx(classes._infoheading, classes._margintop80)} variant="h5">
          {t("Dependency Rules")}
        </Typography>
        <div className={classes._basicdetailhead}>
          <div>
            <Typography className={clsx(classes._head, classes._name)} variant="h6">
              {t("Depends On Permissions")}
            </Typography>
            <Box mt={1} display="flex" flexWrap="wrap">
              {dependsOn.length > 0 ? (
                dependsOn.map((name) => (
                  <span key={name} className={classes.ruleBadge}>
                    {getPermissionLabelByName(name)}
                  </span>
                ))
              ) : (
                <Typography className={classes._detail} variant="h6">
                  -
                </Typography>
              )}
            </Box>
          </div>
          {dependsOn.length > 0 && (
            <div>
              <Typography className={clsx(classes._head, classes._name)} variant="h6">
                {t("Operator")}
              </Typography>
              <Typography className={clsx(classes._name, classes._detail)} variant="h6">
                {t(operator)}
              </Typography>
            </div>
          )}
          <div>
            <Typography className={clsx(classes._head, classes._name)} variant="h6">
              {t("Exclusive With")}
            </Typography>
            <Box mt={1} display="flex" flexWrap="wrap">
              {exclusiveWith.length > 0 ? (
                exclusiveWith.map((name) => (
                  <span key={name} className={classes.ruleBadge}>
                    {getPermissionLabelByName(name)}
                  </span>
                ))
              ) : (
                <Typography className={classes._detail} variant="h6">
                  -
                </Typography>
              )}
            </Box>
          </div>
        </div>
      </DarkLayout>
    </>
  );
};

export default PermissionDetail;
